import React from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { nestedSurfaceSx, toneMain } from "./channelTheme";
import { formatChannelValue, formatHours, formatNumber } from "./channelUtils";

function Kpi({ label, value, caption, tone }) {
  return (
    <MDBox sx={(theme) => ({ ...nestedSurfaceSx(theme), p: 1.5, minWidth: 0 })}>
      <MDTypography variant="caption" color="text" display="block" noWrap>
        {label}
      </MDTypography>
      <MDTypography
        variant="h5"
        fontWeight="bold"
        lineHeight={1.2}
        noWrap
        sx={({ palette }) => (tone ? { color: `${toneMain(palette, tone)} !important` } : {})}
      >
        {value}
      </MDTypography>
      {caption && (
        <MDTypography variant="caption" color="text" display="block" noWrap sx={{ opacity: 0.75 }} title={caption}>
          {caption}
        </MDTypography>
      )}
    </MDBox>
  );
}

export const forecastKpi = (channel) => {
  const f = channel?.forecast || { status: "none" };
  switch (f.status) {
    case "none":
      return { value: "—", caption: "No thresholds set" };
    case "insufficient":
      return { value: "—", caption: "Not enough history" };
    case "crossed":
      return { value: "Now", caption: "Threshold crossed", tone: channel.status.level === "alarm" ? "error" : "warning" };
    case "flat":
      return { value: "—", caption: "No crossing projected" };
    case "eta": {
      const t = f.threshold;
      return {
        value: `~${formatHours(f.hours)}`,
        caption: `to ${t.label} (${t.operator} ${formatChannelValue(t.value, channel.unit, channel.kind)})`,
        tone: f.hours < 12 ? (t.severity === "alarm" ? "error" : "warning") : undefined,
      };
    }
    default:
      return { value: "—" };
  }
};

export default function ChannelKpiRow({ channel }) {
  if (!channel) return null;
  const forecast = forecastKpi(channel);
  const s = channel.stats24;
  const kpis =
    channel.kind === "level"
      ? [
          { label: "Level", value: formatChannelValue(channel.value, "%", "level"), caption: channel.role === "supply" ? "Supply tank" : "Holding tank" },
          {
            label: "Volume",
            value: formatChannelValue(channel.volume, channel.volumeUnit),
            caption: channel.capacity !== null ? `of ${formatChannelValue(channel.capacity, channel.volumeUnit)}` : undefined,
          },
          { label: "Ullage", value: formatChannelValue(channel.ullage, channel.volumeUnit), caption: "Remaining space" },
          {
            label: "Fluid height",
            value: formatChannelValue(channel.fluidHeight, channel.heightUnit),
            caption:
              channel.usableHeight !== null ? `of ${formatChannelValue(channel.usableHeight, channel.heightUnit)} usable` : undefined,
          },
          { label: "Next threshold", ...forecast },
        ]
      : [
          { label: "Current", value: formatChannelValue(channel.value, channel.unit, channel.kind), caption: channel.kindLabel },
          { label: "24h min", value: s ? formatChannelValue(s.min, channel.unit) : "—" },
          { label: "24h max", value: s ? formatChannelValue(s.max, channel.unit) : "—" },
          { label: "24h avg", value: s ? formatChannelValue(s.avg, channel.unit) : "—", caption: s ? `${formatNumber(s.count, 0)} readings` : undefined },
          { label: "Next threshold", ...forecast },
        ];

  return (
    <MDBox display="grid" gap={1.5} gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))">
      {kpis.map((k) => (
        <Kpi key={k.label} {...k} />
      ))}
    </MDBox>
  );
}
